<template>
    <div id="patients-portal-form">
        <div class="page-height">
            <b-row>
                <b-col class="col-12">
                    <MdTitleRegInfo
                        v-if="!isAllPatientsMode"
                        v-bind:RegistrationInfo="PatientTitle"
                        v-bind:titlePrefix="$t('PPDbContacts.Title')"
                        v-bind:titleRef="'titleElementPatientContacts'"
                        v-bind:patientCode="patientCode"
                    >
                    </MdTitleRegInfo>

                    <MdTitle
                        v-else
                        v-bind:Text="$t('PPDbContacts.Title')"
                        v-bind:Name="'titleElementPatientContacts'"
                    />

                    <div
                        class="alert alert-fill-warning d-flex align-items-center m-3"
                        role="alert"
                        v-if="isReadOnly"
                    >
                        <i class="mdi mdi-alert-circle me-2" style="font-size: 1.5rem;"></i>
                        <div class="flex-grow-1">
                            <span>{{ $t('Shared.ReadOnlyPermission') }}</span>
                        </div>
                    </div>
                </b-col>
            </b-row>

            <b-row>
                <b-col lg="3" md="3" sm="12">
                    <MdButton
                        v-bind:Label="$t('ContactsList.AddContact')"
                        v-bind:ClassProp="
                            this.isReadOnly
                                ? 'btn btn-primary my-3 btn-block'
                                : 'btn btn-primary my-3 btn-block btn-login'
                        "
                        v-bind:IconClass="'ti-plus pr-1'"
                        v-on:click="addContactToPatient"
                        v-bind:isReadOnly="this.isReadOnly"
                        v-bind:Name="'btn_add_order'"
                    >
                    </MdButton>
                </b-col>
                <b-col lg="3" md="3" sm="12">
                    <MdSelect
                        v-bind:Label="$t('Shared.PortalStatus')"
                        v-bind:Options="accessToPortalOptionsList"
                        v-bind:FieldDescription="'text'"
                        v-bind:FieldCode="'value'"
                        v-bind:ModelArray="accessToPortalModel"
                        v-bind:Multiple="false"
                        :Input="value => changeDropDown(value, 'accessToPortal')"
                    />
                </b-col>
                <b-col lg="3" md="3" sm="12">
                    <MdSelect
                        v-bind:Label="$t('Shared.ReceiveNotification')"
                        v-bind:Options="yesNoAllOptionsList"
                        v-bind:FieldDescription="'text'"
                        v-bind:FieldCode="'value'"
                        v-bind:ModelArray="receiveNotificationModel"
                        v-bind:Multiple="false"
                        :Input="value => changeDropDown(value, 'portalNotifications')"
                    />
                </b-col>
                <b-col lg="3" md="3" sm="12">
                    <MdSelect
                        v-bind:Label="$t('Shared.ReceiveAppointment')"
                        v-bind:Options="yesNoAllOptionsList"
                        v-bind:FieldDescription="'text'"
                        v-bind:FieldCode="'value'"
                        v-bind:ModelArray="receiveAppointmentModel"
                        v-bind:Multiple="false"
                        :Input="value => changeDropDown(value, 'appointmentNotifications')"
                    />
                </b-col>
            </b-row>

            <MdTitle
                v-bind:Text="$t('Shared.SearchResult')"
                v-bind:isSubtitle="true"
                v-bind:Name="'T_TitleSearch'"
            />

            <b-row>
                <b-col class="col-12 mt-2">
                    <MdTable
                        v-bind:fields="fields"
                        v-bind:items="items"
                        v-bind:totalRows="totalRows"
                        v-bind:showMessageIsEmpty="showMessageIsEmpty"
                        v-bind:currentPage="currentPage"
                        v-bind:perPage="perPage"
                        v-bind:pageOptions="pageOptions"
                        v-bind:VisibleEditIcon="true"
                        v-bind:FieldsDate="fieldsDateArr"
                        :pageChanged="pageChanged"
                        :perPageFunction="perPageFunction"
                        v-bind:multiSelect="true"
                        v-bind:ShowSelectAll="true"
                        v-bind:ShowSelectedCount="true"
                        v-bind:ShowSearchCriteria="true"
                        v-bind:SelectionKey="'contactCode'"
                        v-bind:SelectAllFunction="getAllContactForSelectAll"
                        :onSearchCriteria="onSearchCriteriaChanged"
                        :editIconEvent="editContactHandler"
                        :customActionButtons="customActionButtons"
                        @selectionChanged="selectionChanged"
                        :isReadOnly="isReadOnly"
                        ref="contactsTable"
                    >
                        <template #cell-endDateforActiveRelease="{ cell }" class="dateNotes">
                            {{ cell.value }}<br />
                            <div class="pushItDown"></div>
                            <span
                                v-if="isExpired(cell.data.endDateforActiveRelease)"
                                class="DocumentExpired DocumentExpiredRed"
                            >
                                {{ $t('Shared.Expired') }}
                            </span>
                        </template>

                        <template #cell-portalAccessTranslated="{ cell }">
                            <span v-if="cell.data.portalAccess" class="DocumentExpired">
                                {{ cell.data.portalAccessTranslated }}
                            </span>
                            <span v-else>{{ cell.data.portalAccessTranslated }}</span>
                        </template>
                    </MdTable>
                </b-col>
            </b-row>
        </div>

        <div class="space-actions"></div>
        <div class="page-actions">
            <MdButton
                v-if="isAllPatientsMode"
                v-bind:Label="$t('Shared.Back')"
                v-bind:Name="'btn_back_patients_portal'"
                v-bind:ClassProp="'secondary mx-2 btn-login'"
                v-on:click="gotoPatientPortal"
            />
            <b-dropdown
                id="dropdown-dropup"
                dropup
                :text="$tc('Shared.ActionsTxt', 2)"
                variant="primary"
                class="m-2"
                :disabled="!buttonActionEnabled"
            >
                <b-dropdown-item
                    href="#"
                    :disabled="grandAccessToPortalDisabled"
                    v-on:click="() => grandAccessToPortalHandler(null)"
                >
                    {{ $t('Contact.gPortalAccess') }}
                </b-dropdown-item>
                <b-dropdown-item
                    href="#"
                    :disabled="setPortalNotificationDisabled"
                    v-on:click="() => setPortalNotificationHandler(null)"
                >
                    {{ $t('PPDbContacts.sPortalNotification') }}
                </b-dropdown-item>
                <b-dropdown-item
                    href="#"
                    :disabled="setAppointmentsNotificationDisabled"
                    v-on:click="() => setAppointmentsNotificationHandler(null)"
                >
                    {{ $t('PPDbContacts.sAppointmentsNotification') }}
                </b-dropdown-item>
                <b-dropdown-item
                    href="#"
                    :disabled="removeAccessToPortalDisabled"
                    v-on:click="() => removeAccessToPortalHandler(null)"
                >
                    {{ $t('PPDbContacts.rPortalAccess') }}
                </b-dropdown-item>
                <b-dropdown-item
                    href="#"
                    :disabled="removePortalNotificationDisabled"
                    v-on:click="() => removePortalNotificationHandler(null)"
                >
                    {{ $t('PPDbContacts.rPortalNotification') }}
                </b-dropdown-item>
                <b-dropdown-item
                    href="#"
                    :disabled="removeAppointmentsNotificationDisabled"
                    v-on:click="() => removeAppointmentsNotificationHandler(null)"
                >
                    {{ $t('PPDbContacts.rAppointmentsNotification') }}
                </b-dropdown-item>
            </b-dropdown>
        </div>

        <patient-portal-change-password
            :visible.sync="changeContactPasswordVisible"
            :contactCode="selectedContactCode"
            :contactName="selectedContactName"
            :userName="selectedContactUserName"
            :toContact="true"
        ></patient-portal-change-password>

        <patient-portal-select-patient
            :visible.sync="selectPatientVisible"
            @ok="onPatientSelectedForAddContact"
        ></patient-portal-select-patient>

        <patient-portal-change-password
            :visible.sync="changePasswordVisible"
            :userName="selectedUserName"
        />

        <patient-portal-create-login
            :visible.sync="createLoginVisible"
            :patientCode="patientCodeFirstOrDefault"
            :fullName="selectedFullName"
            :email="selectedEmail"
            @ok="createLoginHandler"
        />

        <patient-portal-change-user-name
            :visible.sync="changeUserNameVisible"
            :patientCode="patientCodeFirstOrDefault"
            :fullName="selectedFullName"
            :userName="selectedUserName"
            @ok="createLoginHandler"
        />

        <patient-portal-notify
            :visible.sync="notifyVisible"
            :patientCodeList="selectedPatientCodeList"
            :notifyType="notifyType"
            :notifyTemplateList="
                notifyType === 'email' ? notifyEmailTemplateList : notifyTextTemplateList
            "
        />
    </div>
</template>

<script>
import EnumService from '@/services/enum.service';
import ContactService from '@/services/contact.service';
import Utils from '@/common/utils';
import { EventBus } from '@/event-bus.js';
import PatientPortalsService from '@/services/patientportals.service';
import { patientsPortalStore } from '../patientsPortalStore';
import PatientsPortalBase from '../patientsPortalBase.vue';
import PatientPortalChangePassword from '../dialogs/patientPortalChangePassword.vue';
import PatientPortalSelectPatient from '../dialogs/patientPortalSelectPatient.vue';
import PatientPortalCreateLogin from '../dialogs/patientPortalCreateLogin.vue';
import PatientPortalChangeUserName from '../dialogs/patientPortalChangeUserName.vue';
import PatientPortalNotify from '../dialogs/patientPortalNotify.vue';

export default {
    name: 'PatientsPortalContactList',
    extends: PatientsPortalBase,
    components: {
        PatientPortalChangePassword,
        PatientPortalSelectPatient,
        PatientPortalCreateLogin,
        PatientPortalChangeUserName,
        PatientPortalNotify
    },
    data() {
        return {
            RightMenuList: JSON.parse(JSON.stringify(EnumService.PatientsPortalSummaryItems)),
            items: [],
            totalRows: 0,
            pageOptions: [5, 10, 15],
            currentPage: 1,
            perPage: 10,
            selectPatientVisible: false,
            contactFields: [
                {
                    key: 'contactName',
                    sortable: true,
                    label: this.$t('ContactsList.ContactName'),
                    class: 'text-left'
                },
                {
                    key: 'dob',
                    sortable: true,
                    label: this.$t('PPDbContacts.ContactDOB'),
                    class: 'text-center',
                    formatter: value => {
                        return value ? Utils.formatterDateToString(value) : '';
                    }
                },
                {
                    key: 'portalAccessTranslated',
                    sortable: true,
                    label: this.$t('PPDbContacts.PortalAccess'),
                    class: 'text-left'
                },
                {
                    key: 'contactUserName',
                    sortable: true,
                    label: this.$t('PPDbContacts.ContactUserName'),
                    class: 'text-left'
                },
                {
                    key: 'releaseOnFileTranslated',
                    sortable: true,
                    label: this.$t('Shared.ReleaseOnFile'),
                    class: 'text-left'
                },
                {
                    key: 'endDateforActiveRelease',
                    sortable: true,
                    label: this.$t('Contact.ActiveReleaseOnFileEndDate'),
                    class: 'text-center',
                    formatter: value => {
                        return value ? Utils.formatterDateToString(value) : '';
                    }
                },
                {
                    key: 'portalNotificationsTranslated',
                    sortable: true,
                    label: this.$t('Shared.PortalNotifications'),
                    class: 'text-left'
                },
                {
                    key: 'appointmentNotificationsTranslated',
                    sortable: true,
                    label: this.$t('PPDbContacts.Appointment'),
                    class: 'text-left'
                },
                {
                    key: 'email',
                    sortable: true,
                    label: this.$t('Shared.Email'),
                    class: 'text-left'
                },
                {
                    key: 'cellPhone',
                    sortable: true,
                    label: this.$t('Shared.CellPhone'),
                    class: 'text-left'
                }
            ],
            fieldsDateArr: ['patientDob', 'contactDOB', 'endDateforActiveRelease'],
            showMessageIsEmpty: false,
            selectedContactUserName: '',
            selectedContactCode: '',
            selectedContactName: '',
            parameters: {
                searchCriteria: '',
                accessToPortal: null,
                portalNotifications: null,
                appointmentNotifications: null
            },
            dirInput: this.$i18n.locale === 'ar' ? 'rtl' : 'ltr',
            yesNoAllOptionsList: EnumService.YesNoAllOptions,
            accessToPortalOptionsList: EnumService.AccessToPortalOptions,
            accessToPortalModel: { value: null, text: this.$t('Shared.All') },
            receiveNotificationModel: { value: null, text: this.$t('Shared.All') },
            receiveAppointmentModel: { value: null, text: this.$t('Shared.All') },
            patientCode: this.$route.params.id,
            PatientTitle: {
                programName: '',
                fullName: '',
                admitDate: '',
                dischargeDate: '',
                dob: '',
                activitytypename: '',
                startdatetime: '',
                ageFormatted: ''
            },
            isReadOnly: this.$store.state.isPatientPortalContactsEHRReadOnly,
            changeContactPasswordVisible: false,
            selectedContacts: [],
            customActionButtons: [
                {
                    id: 'change-password',
                    visible: true,
                    disabled: this.changePasswordDisabled,
                    icon: 'med-change-pass',
                    handler: this.changePasswordHandler,
                    tooltip: this.$t('Shared.ChangePassword')
                },
                {
                    id: 'grand-access-portal',
                    visible: this.grandAccessToPortalVisible,
                    disabled: this.accessToPatientPortalDisabled,
                    icon: 'med-unlock',
                    class: item => (item.portalAccess ? 'checkedBTN' : 'uncheckedBTN'),
                    handler: this.grandAccessToPortalHandler,
                    tooltip: this.$t('Contact.gPortalAccess')
                },
                {
                    id: 'remove-access-portal',
                    visible: this.removeAccessToPortalVisible,
                    disabled: this.accessToPatientPortalDisabled,
                    icon: 'med-lock',
                    class: item => (item.portalAccess ? 'checkedBTN' : 'uncheckedBTN'),
                    handler: this.removeAccessToPortalHandler,
                    tooltip: this.$t('PPDbContacts.rPortalAccess')
                },
                {
                    id: 'set-portal-notification',
                    visible: this.setPortalNotificationVisible,
                    disabled: this.accessToNotificationPatientPortalDisabled,
                    icon: 'med-portal-notify',
                    class: item => (item.portalNotifications ? 'checkedBTN' : 'uncheckedBTN'),
                    handler: this.setPortalNotificationHandler,
                    tooltip: this.$t('PPDbContacts.sPortalNotification')
                },
                {
                    id: 'remove-portal-notification',
                    visible: this.removePortalNotificationVisible,
                    disabled: this.accessToNotificationPatientPortalDisabled,
                    icon: 'med-portal-notify',
                    class: item => (item.portalNotifications ? 'checkedBTN' : 'uncheckedBTN'),
                    handler: this.removePortalNotificationHandler,
                    tooltip: this.$t('PPDbContacts.rPortalNotification')
                },
                {
                    id: 'set-appointments-notification',
                    visible: this.setAppointmentsNotificationVisible,
                    disabled: this.accessToPatientPortalDisabled,
                    icon: 'med-scheduling-notify',
                    class: item => (item.appointmentNotifications ? 'checkedBTN' : 'uncheckedBTN'),
                    handler: this.setAppointmentsNotificationHandler,
                    tooltip: this.$t('PPDbContacts.sAppointmentsNotification')
                },
                {
                    id: 'remove-appointments-notification',
                    visible: this.removeAppointmentsNotificationVisible,
                    disabled: this.accessToPatientPortalDisabled,
                    icon: 'med-scheduling-notify',
                    class: item => (item.appointmentNotifications ? 'checkedBTN' : 'uncheckedBTN'),
                    handler: this.removeAppointmentsNotificationHandler,
                    tooltip: this.$t('PPDbContacts.rAppointmentsNotification')
                }
            ]
        };
    },

    computed: {
        isAllPatientsMode() {
            return !this.patientCode;
        },

        fields() {
            if (!this.isAllPatientsMode) {
                return this.contactFields;
            }

            return [
                {
                    key: 'patientName',
                    sortable: true,
                    label: this.$tc('Shared.Patient', 1),
                    class: 'text-left'
                },
                {
                    key: 'patientCode',
                    sortable: true,
                    label: this.$t('Shared.PatientCode'),
                    class: 'text-left'
                },
                {
                    key: 'patientDob',
                    sortable: true,
                    label: this.$t('PPDbContacts.PatientDOB'),
                    class: 'text-center',
                    formatter: value => {
                        return value ? Utils.formatterDateToString(value) : '';
                    }
                },
                ...this.contactFields
            ];
        },

        buttonActionEnabled() {
            return !this.isReadOnly && this.selectedContacts.length !== 0;
        },

        grandAccessToPortalDisabled() {
            return (
                this.selectedContacts.length === 0 ||
                this.selectedContacts.every(item => !this.grandAccessToPortalVisible(item))
            );
        },

        removeAccessToPortalDisabled() {
            return (
                this.selectedContacts.length === 0 ||
                this.selectedContacts.every(item => !this.removeAccessToPortalVisible(item))
            );
        },

        setPortalNotificationDisabled() {
            return (
                this.selectedContacts.length === 0 ||
                this.selectedContacts.every(item => !this.setPortalNotificationVisible(item))
            );
        },

        removePortalNotificationDisabled() {
            return (
                this.selectedContacts.length === 0 ||
                this.selectedContacts.every(item => !this.removePortalNotificationVisible(item))
            );
        },

        setAppointmentsNotificationDisabled() {
            return (
                this.selectedContacts.length === 0 ||
                this.selectedContacts.every(item => !this.setAppointmentsNotificationVisible(item))
            );
        },

        removeAppointmentsNotificationDisabled() {
            return (
                this.selectedContacts.length === 0 ||
                this.selectedContacts.every(
                    item => !this.removeAppointmentsNotificationVisible(item)
                )
            );
        }
    },

    methods: {
        getActiveElement() {
            return 'PPDB_PatientContacts';
        },

        createLoginHandler(item) {
            this.selectedItems = [item];
            this.enabledAllItems();
        },

        isExpired(date) {
            const result = Utils.isExpired(date);
            return result;
        },

        gotoPatientPortal() {
            this.$router.push({ name: 'patients-portal' });
        },

        changeDropDown(value, field) {
            this.parameters[field] = value ? value.value : null;
            this.searchContacts();
        },

        addContactToPatient() {
            if (this.isAllPatientsMode) {
                this.selectPatientVisible = true;
                return;
            }

            this.$router.push({
                name: 'patient-portal-contact-add',
                params: { id: this.patientCode }
            });
        },

        onPatientSelectedForAddContact(patientCode) {
            this.$router.push({
                name: 'patient-portal-contact-add',
                params: { id: patientCode },
                query: this.isAllPatientsMode ? { all: '1' } : {}
            });
        },

        editContactHandler(item) {
            this.$router.push({
                name: 'patient-portal-contact-edit',
                params: { id: this.patientCode || item.patientCode, contactid: item.contactCode },
                query: this.isAllPatientsMode ? { all: '1' } : {}
            });
        },

        changePasswordDisabled(item) {
            let dateValid = true;
            if (item.endDateforActiveRelease != null) {
                dateValid =
                    Utils.toMidnight(Utils.getCurrentDateTime()) <=
                    Utils.toMidnight(item.endDateforActiveRelease);
            }

            const enabled = item.portalAccess && dateValid;
            return !enabled;
        },

        changePasswordHandler(item) {
            this.changeContactPasswordVisible = true;
            this.selectedContactCode = item.contactCode;
            this.selectedContactUserName = item.contactUserName;
            this.selectedContactName = item.contactName;
        },

        grandAccessToPortalVisible(item) {
            return !item.portalAccess;
        },

        getContactCodeToUpdate(item) {
            let items = this.selectedContacts.map(item => ({
                patientCode: this.patientCode || item.patientCode,
                contactCode: item.contactCode,
                email: item.email,
                cellPhone: item.cellPhone
            }));

            if (item) {
                items = [
                    {
                        patientCode: this.patientCode || item.patientCode,
                        contactCode: item.contactCode,
                        email: item.email,
                        cellPhone: item.cellPhone
                    }
                ];
            }

            return items;
        },

        accessToPatientPortalDisabled(item) {
            let validEndDateForActiveRelease = false;
            if (!Utils.isNullOrEmptyString(item.endDateforActiveRelease)) {
                validEndDateForActiveRelease =
                    Utils.toMidnight(Utils.getCurrentDateTime()) <=
                    Utils.toMidnight(item.endDateforActiveRelease);
            }

            const valid =
                !Utils.isNullOrEmptyString(item.email) &&
                item.releaseOnFile &&
                validEndDateForActiveRelease;

            return !valid;
        },

        accessToNotificationPatientPortalDisabled(item) {
            const valid =
                !Utils.isNullOrEmptyString(item.cellPhone) &&
                !this.accessToPatientPortalDisabled(item);

            return !valid;
        },

        grandAccessToPortalHandler(item) {
            this.updateContactNotification({
                contacts: this.getContactCodeToUpdate(item),
                accessToPortal: true
            });
        },

        removeAccessToPortalVisible(item) {
            return item.portalAccess;
        },

        removeAccessToPortalHandler(item) {
            this.updateContactNotification({
                contacts: this.getContactCodeToUpdate(item),
                accessToPortal: false
            });
        },

        setPortalNotificationVisible(item) {
            return !item.portalNotifications;
        },

        setPortalNotificationHandler(item) {
            this.updateContactNotification({
                contacts: this.getContactCodeToUpdate(item),
                portalNotifications: true
            });
        },

        removePortalNotificationVisible(item) {
            return item.portalNotifications;
        },

        removePortalNotificationHandler(item) {
            this.updateContactNotification({
                contacts: this.getContactCodeToUpdate(item),
                portalNotifications: false
            });
        },

        setAppointmentsNotificationVisible(item) {
            return !item.appointmentNotifications;
        },

        setAppointmentsNotificationHandler(item) {
            this.updateContactNotification({
                contacts: this.getContactCodeToUpdate(item),
                appointmentNotifications: true
            });
        },

        removeAppointmentsNotificationVisible(item) {
            return item.appointmentNotifications;
        },

        removeAppointmentsNotificationHandler(item) {
            this.updateContactNotification({
                contacts: this.getContactCodeToUpdate(item),
                appointmentNotifications: false
            });
        },

        selectionChanged(items) {
            this.selectedContacts = items;
        },

        pageChanged(page) {
            this.currentPage = page;
            this.listingAsync();
        },

        perPageFunction(perPage) {
            this.perPage = perPage;
            this.currentPage = 1;
            this.listingAsync();
        },

        searchContacts() {
            this.currentPage = 1;
            this.$refs.contactsTable.clearSelection();
            this.listingAsync();
        },

        onSearchCriteriaChanged(criteria) {
            this.parameters.searchCriteria = criteria;
            this.searchContacts();
        },

        async getAllContactForSelectAll(searchCriteria) {
            const parameters = {
                PatientCode: this.patientCode,
                searchCriteria:
                    searchCriteria !== undefined ? searchCriteria : this.parameters.searchCriteria,
                accessToPortal: this.parameters.accessToPortal,
                portalNotifications: this.parameters.portalNotifications,
                appointmentNotifications: this.parameters.appointmentNotifications
            };
            const response = await ContactService.getPatientPortalAll(parameters);

            if (response.status === 200 && response.data) {
                return response.data;
            }

            return [];
        },

        async listingAsync() {
            const parameters = {
                PatientCode: this.patientCode,
                PageSize: this.perPage,
                PageNumber: this.currentPage,
                ...this.parameters
            };

            ContactService.getPatientPortalAll(parameters)
                .then(response => {
                    if (response.status == '200' && response.data) {
                        this.items = response.data;
                        let headerString = response.headers['x-pagination'];
                        const header = JSON.parse(headerString);
                        this.totalRows = header.TotalCount;
                    }

                    this.showMessageIsEmpty = this.totalRows === 0;
                })
                .catch(error => {
                    this.showResponseErrorMessage(error);
                });
        },

        updateContactNotification(params) {
            ContactService.updateContactNotification({ PatientCode: this.patientCode, ...params })
                .then(response => {
                    if (response.status == '200') {
                        if (response.data) {
                            const items = response.data;
                            items.forEach(contact => {
                                const indexOf = this.items.findIndex(
                                    item => item.contactCode === contact.contactCode
                                );

                                if (indexOf !== -1) {
                                    this.$set(this.items, indexOf, contact);
                                }
                            });

                            if (items.length === params.contacts.length) {
                                this.showMessage(
                                    this.$t('Msg.Success'),
                                    this.$t('Shared.SavedSuccessfully'),
                                    'success'
                                );
                            } else {
                                let message = this.$t('Shared.SavedSuccessfully');
                                let messageType = 'success';

                                if (
                                    Object.prototype.hasOwnProperty.call(
                                        params,
                                        'accessToPortal'
                                    ) &&
                                    params['accessToPortal']
                                ) {
                                    message = this.$t(
                                        'PPDbContacts.PartialUpdateGrantAccessMessage',
                                        {
                                            to: items.length,
                                            from: params.contacts.length
                                        }
                                    );
                                    messageType = 'warning';
                                } else if (
                                    (Object.prototype.hasOwnProperty.call(
                                        params,
                                        'portalNotifications'
                                    ) &&
                                        params['portalNotifications']) ||
                                    (Object.prototype.hasOwnProperty.call(
                                        params,
                                        'appointmentNotifications'
                                    ) &&
                                        params['appointmentNotifications'])
                                ) {
                                    message = this.$t(
                                        'PPDbContacts.PartialUpdateNotificationMessage',
                                        {
                                            to: items.length,
                                            from: params.contacts.length
                                        }
                                    );
                                    messageType = 'warning';
                                }

                                this.showMessage(
                                    this.$t('PPDbContacts.Title'),
                                    message,
                                    messageType
                                );
                            }
                        }
                    }
                })
                .catch(error => {
                    this.showResponseErrorMessage(error);
                });
        },

        async loadPatientTitle(patientCode) {
            const infoList = await this.getInfoTitle(patientCode);
            if (infoList && infoList.length > 0) {
                const { dob, firstname, lastname, age, ageFormatted } = infoList[0];
                if (ageFormatted) this.PatientTitle.ageFormatted = ageFormatted;
                if (dob) this.PatientTitle.dob = Utils.formatterDateToString(dob);
                if (age) this.PatientTitle.age = age;
                if (firstname) this.PatientTitle.fullName = `${firstname} ${lastname}`;
            }
        },

        async loadPortalPatient(patientCode) {
            const response = await PatientPortalsService.getAllDashboard({
                PageSize: 1,
                PageNumber: 1,
                PatientCodeList: [patientCode],
                status: null,
                portalStatus: null
            });
            if (response.status === 200 && response.data && response.data.length > 0) {
                this.selectedItems = response.data.filter(
                    x => x.patientCode.trim() === patientCode.trim()
                );
            }
        },

        enabledAllItems() {
            const item = this.selectedItems.length === 1 ? this.selectedItems[0] : null;
            this.RightMenuList.forEach(element => {
                let buttonEnabled = false;
                switch (element.value) {
                    case 'PPDB_CreateCredentials':
                        buttonEnabled =
                            item &&
                            item.portalStatus === 'Not Enrolled' &&
                            Utils.isNullOrEmptyString(item.userName) &&
                            !Utils.isNullOrEmptyString(item.email) &&
                            !!item.dob;
                        break;
                    case 'PPDB_ChangeUsername':
                    case 'PPDB_ChangePassword':
                        buttonEnabled = item && !Utils.isNullOrEmptyString(item.userName);
                        break;
                    case 'PPDB_PatientForms':
                    case 'PPDB_PatientContacts':
                    case 'PPDB_PatientClaims':
                    case 'PPDB_PatientInfo':
                    case 'PPDB_Summary':
                    case 'PPDB_ChangePatInfo':
                    case 'PPDB_Messages':
                    case 'PPDB_PatientList':
                        buttonEnabled = true;
                        break;
                    case 'PPDB_EmailToPatient':
                        buttonEnabled = this.isNotify('email');
                        break;
                    case 'PPDB_TextToPatient':
                        buttonEnabled = this.isNotify('text');
                        break;
                    default:
                        break;
                }
                element.ReadOnly = !buttonEnabled;
            });

            this.OptionsRightMenu.visible = false;
            this.OptionsRightMenu.btnPatientsPortalDashboardVisible = true;
            this.OptionsRightMenu.ItemsList = this.RightMenuList;
            this.OptionsRightMenu.active_el = this.getActiveElement();
            this.$emit('onMenuRightBarPatientsPortalDashboard', this.OptionsRightMenu);
        }
    },

    async mounted() {
        const patientCode = this.patientCode;

        if (patientCode) {
            await this.$nextTick();
            EventBus.$emit('loadMenu', patientCode);
            try {
                const stored = patientsPortalStore.selectedItem;
                if (stored && stored.patientCode.trim() === patientCode.trim()) {
                    this.selectedItems = [stored];
                    patientsPortalStore.selectedItem = null;
                    await this.loadPatientTitle(patientCode);
                } else {
                    await Promise.all([
                        this.loadPatientTitle(patientCode),
                        this.loadPortalPatient(patientCode)
                    ]);
                }
            } catch (e) {
                // fall through to enabledAllItems with whatever data loaded
            }

            this.enabledAllItems();
        } else {
            this.OptionsRightMenu.visible = false;
            this.OptionsRightMenu.btnPatientsPortalDashboardVisible = false;
            this.OptionsRightMenu.ItemsList = [];
            this.$emit('onMenuRightBarPatientsPortalDashboard', this.OptionsRightMenu);
        }

        await this.listingAsync();
    }
};
</script>
